import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Key, ArrowLeft } from 'lucide-react';

export default function LicenseRegister() {
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', password: '', password_confirm: '', license_key: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.password_confirm) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/api/auth/register-with-license`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          password: formData.password,
          license_key: formData.license_key
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Registrierung fehlgeschlagen');
      toast.success('Konto erstellt! Sie können sich jetzt anmelden.');
      navigate('/login');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-12 h-12 bg-[#9B1B30]/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <Key className="w-6 h-6 text-[#9B1B30]" />
          </div>
          <CardTitle className="font-barlow text-xl">Mit Lizenz registrieren</CardTitle>
          <p className="text-sm text-gray-500 mt-1">Geben Sie Ihren Lizenzschlüssel ein, um ein Konto zu erstellen</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Lizenzschlüssel</Label>
              <Input
                value={formData.license_key}
                onChange={(e) => setFormData(prev => ({ ...prev, license_key: e.target.value.toUpperCase() }))}
                placeholder="FW-XXXX-XXXX-XXXX"
                className="font-mono text-center tracking-wider"
                required
                data-testid="license-key-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Vorname</Label>
                <Input value={formData.first_name} onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))} required data-testid="license-first-name" />
              </div>
              <div>
                <Label>Nachname</Label>
                <Input value={formData.last_name} onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))} required data-testid="license-last-name" />
              </div>
            </div>
            <div>
              <Label>E-Mail</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} required data-testid="license-email" />
            </div>
            <div>
              <Label>Passwort</Label>
              <Input type="password" value={formData.password} onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))} required data-testid="license-password" />
            </div>
            <div>
              <Label>Passwort bestätigen</Label>
              <Input type="password" value={formData.password_confirm} onChange={(e) => setFormData(prev => ({ ...prev, password_confirm: e.target.value }))} required data-testid="license-password-confirm" />
            </div>
            <Button type="submit" className="w-full bg-[#9B1B30] hover:bg-[#7d1526]" disabled={loading} data-testid="license-register-btn">
              {loading ? 'Wird erstellt...' : 'Konto erstellen'}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-[#9B1B30] hover:underline flex items-center justify-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Zurück zum Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
